import * as chatHistoryStatusFilter from './chat_history_status_filter';

type CustomHistoryFilterClause =
  chatHistoryStatusFilter.CustomHistoryFilterClause;

type HistoryFilterStoreProperty =
  | 'enableHistoryStatusFilter'
  | 'historyStatusFilter'
  | 'customHistoryFilter';

type HistoryFilterStoreValue =
  | boolean
  | chatHistoryStatusFilter.HistoryStatusFilterValue
  | CustomHistoryFilterClause[]
  | undefined;

type HistoryFilterStoreUpdater = (
  property: HistoryFilterStoreProperty,
  data: HistoryFilterStoreValue,
) => void;

interface HistoryFilterParameters {
  readonly enableHistoryStatusFilter?: unknown;
  readonly historyStatusFilter?: unknown;
  readonly customHistoryFilter?:
    | readonly { readonly key?: unknown; readonly value?: unknown }[]
    | null;
}

export const normalizeCustomHistoryFilterClauses = (
  clauses:
    | readonly { readonly key?: unknown; readonly value?: unknown }[]
    | null
    | undefined,
): CustomHistoryFilterClause[] => {
  if (!Array.isArray(clauses) || !clauses.length) {
    return [];
  }

  const deduped = new Map<string, string>();
  clauses.forEach((c) => {
    const key = String(c?.key ?? '').trim();
    const value = String(c?.value ?? '').trim();
    if (key.length > 0 && value.length > 0) {
      deduped.delete(key);
      deduped.set(key, value);
    }
  });

  return Array.from(deduped, ([key, value]) => ({ key, value }));
};

export const customHistoryFilterToSessionCustom = (
  clauses: readonly CustomHistoryFilterClause[] | undefined,
): Record<string, string> | undefined => {
  const normalized = normalizeCustomHistoryFilterClauses(clauses);
  if (!normalized.length) {
    return undefined;
  }

  return normalized.reduce<Record<string, string>>((acc, clause) => {
    acc[clause.key] = clause.value;
    return acc;
  }, {});
};

export const normalizeHistoryFilterParameters = (
  parameters?: HistoryFilterParameters | null,
) => {
  const rawEnableHistoryStatusFilter = parameters?.enableHistoryStatusFilter;
  const customHistoryFilter = normalizeCustomHistoryFilterClauses(
    parameters?.customHistoryFilter,
  );
  const enableHistoryStatusFilter =
    rawEnableHistoryStatusFilter === undefined
      ? undefined
      : rawEnableHistoryStatusFilter === true ||
        rawEnableHistoryStatusFilter === 'true' ||
        rawEnableHistoryStatusFilter === 1 ||
        rawEnableHistoryStatusFilter === '1';
  const historyStatusFilter =
    parameters?.historyStatusFilter === undefined
      ? undefined
      : chatHistoryStatusFilter.parseHistoryStatusFilterValue(
          parameters.historyStatusFilter,
        );
  let normalizedCustomHistoryFilter: CustomHistoryFilterClause[] | undefined;
  if (parameters?.customHistoryFilter !== undefined) {
    normalizedCustomHistoryFilter = customHistoryFilter.length
      ? customHistoryFilter
      : undefined;
  }

  return {
    enableHistoryStatusFilter,
    historyStatusFilter,
    customHistoryFilter: normalizedCustomHistoryFilter,
  };
};

export const syncHistoryFilterStore = (
  updateStore: HistoryFilterStoreUpdater,
  parameters?: HistoryFilterParameters | null,
) => {
  const historyFilterParameters = normalizeHistoryFilterParameters(parameters);
  if (historyFilterParameters.enableHistoryStatusFilter !== undefined) {
    updateStore(
      'enableHistoryStatusFilter',
      historyFilterParameters.enableHistoryStatusFilter,
    );
  }
  if (historyFilterParameters.historyStatusFilter !== undefined) {
    updateStore(
      'historyStatusFilter',
      historyFilterParameters.historyStatusFilter,
    );
  }
  if (parameters?.customHistoryFilter !== undefined) {
    updateStore(
      'customHistoryFilter',
      historyFilterParameters.customHistoryFilter,
    );
  }
};
